import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useCycleSummary, useCycleDues, type DueRow, type DueStatus, useScheme } from '../../../../../../features/schemes/network';
import { HeroNumber } from '../../../../../../components/ui/HeroNumber';
import { StatusBadge } from '../../../../../../components/ui/StatusBadge';
import { Button } from '../../../../../../components/ui/Button';
import { RecordPaymentModal } from '../../../../../../features/schemes/components/RecordPaymentModal';
import { CancelReceiptModal } from '../../../../../../features/schemes/components/CancelReceiptModal';
import { SchemeClosureTab } from '../../../../../../features/schemes/components/SchemeClosureTab';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../../../lib/locale';
import { formatMoney } from '@repo/utils';
import { usePageTitle } from '../../../../../../lib/hooks';
import { PageHeader } from '../../../../../../components/layout/PageHeader';
import { Skeleton } from '../../../../../../components/ui/Skeleton';
import Decimal from 'decimal.js';

export const Route = createFileRoute('/_auth/schemes/$id/cycles/$num/dues')({
  component: CycleDuesDashboard,
});

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'PAID', label: 'Paid' },
];

function dueStatusVariant(status: DueStatus): 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'PAID': return 'success';
    case 'PARTIAL': return 'warning';
    case 'OVERDUE': return 'danger';
    default: return 'info';
  }
}

function isSameDayReceipt(collectedAt: string): boolean {
  const today = new Date();
  const collected = new Date(collectedAt);
  return (
    today.getUTCFullYear() === collected.getUTCFullYear() &&
    today.getUTCMonth() === collected.getUTCMonth() &&
    today.getUTCDate() === collected.getUTCDate()
  );
}

function CycleDuesDashboard() {
  const { id: schemeId, num } = Route.useParams();
  usePageTitle(`Cycle ${num} Dues`);
  const cycleNum = parseInt(num, 10);

  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [activeMainTab, setActiveMainTab] = useState<'dues' | 'closure'>('dues');

  const [recordPaymentDue, setRecordPaymentDue] = useState<DueRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{
    receiptId: string;
    receiptNumber: string;
    isSameDay: boolean;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: scheme } = useScheme(schemeId);
  const { data: summary, isLoading: summaryLoading } = useCycleSummary(schemeId, cycleNum);
  const { data: duesData, isLoading: duesLoading, error: duesError, refetch } = useCycleDues(
    schemeId,
    cycleNum,
    page,
    statusFilter || undefined,
  );

  const handleExportCsv = () => {
    if (!duesData?.data) return;
    const headers = ['Subscriber ID', 'Net Due (₹)', 'Paid (₹)', 'Status', 'Last Receipt'];
    const rows = duesData.data.map((d) => [
      d.subscriberId,
      new Decimal(d.netDue).div(100).toFixed(2),
      new Decimal(d.paidAmount).div(100).toFixed(2),
      d.status,
      d.lastReceipt?.receiptNumber ?? '',
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dues-cycle-${num}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title={`Cycle ${num} — Dues`}
        subtitle="Manage subscription dues for this cycle."
        backTo={{ to: "/schemes/$id", params: { id: schemeId } }}
        action={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => window.print()}>
              Print demand notices
            </Button>
            <Button variant="secondary" size="sm" onClick={handleExportCsv}>
              Export CSV
            </Button>
          </div>
        }
      />
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/schemes" className="hover:text-text-primary">Schemes</Link>
        <span>/</span>
        <Link to="/schemes/$id" params={{ id: schemeId }} className="hover:text-text-primary">
          {schemeId.slice(0, 8)}…
        </Link>
        <span>/</span>
        <span className="text-text-primary">Cycle {num} Dues</span>
      </div>
      <div className="grid grid-cols-3 gap-6 p-6 bg-surface-1 border border-border rounded-xl mb-6">
        <HeroNumber
          label="Total due"
          value={summaryLoading ? '…' : formatMoney(summary?.totalDue ?? 0, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
          size="lg"
        />
        <HeroNumber
          label="Total collected"
          value={summaryLoading ? '…' : formatMoney(summary?.totalCollected ?? 0, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
          size="lg"
        />
        <HeroNumber
          label="Outstanding"
          value={summaryLoading ? '…' : String(summary?.outstandingCount ?? 0)}
          size="lg"
        />
      </div>

      {scheme?.status === 'CLOSING' && (
        <div className="flex gap-4 border-b border-border pb-px mb-6">
          <Button
            variant="ghost"
            onClick={() => setActiveMainTab('dues')}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${
              activeMainTab === 'dues'
                ? 'border-accent-500 text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Dues Collection
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveMainTab('closure')}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${
              activeMainTab === 'closure'
                ? 'border-accent-500 text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            Scheme Closure
          </Button>
        </div>
      )}

      {activeMainTab === 'dues' ? (
        <>
          {successMessage && (
            <div className="px-4 py-3 bg-success-bg text-success-text text-sm rounded-lg border border-success-500/20 mb-4 font-semibold">
              {successMessage}
            </div>
          )}

          <div className="flex gap-1 p-1 bg-surface-2 rounded-lg w-fit mb-4">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.value}
                onClick={() => { setStatusFilter(f.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  statusFilter === f.value
                    ? 'bg-surface-1 text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {f.label}
              </Button>
            ))}
          </div>

          <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
            {duesLoading ? (
              <Skeleton rows={5} />
            ) : duesError ? (
              <div className="p-8 text-center">
                <p className="text-sm font-semibold text-danger-600">Failed to load dues</p>
                <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
              </div>
            ) : (
              <table className="w-full border-collapse text-left text-sm">
                <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="p-4">Subscriber</th>
                    <th className="p-4">Net due</th>
                    <th className="p-4">Paid</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Last receipt</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-text-primary">
                  {duesData?.data?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-text-muted">
                        {statusFilter ? `No ${statusFilter.toLowerCase()} dues for this cycle` : 'No dues have been generated for this cycle yet'}
                      </td>
                    </tr>
                  ) : (
                    duesData?.data?.map(due => {
                      const canPay = ['PENDING', 'PARTIAL', 'OVERDUE'].includes(due.status);
                      const canCancel = due.lastReceipt != null && ['PAID', 'PARTIAL'].includes(due.status);
                      return (
                        <tr key={due.subscriberId} className="hover:bg-surface-2 transition-colors">
                          <td className="p-4 text-xs text-text-muted font-mono">{due.subscriberId.slice(0, 8)}…</td>
                          <td className="p-4 font-mono tnum">{formatMoney(due.netDue, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                          <td className="p-4 font-mono tnum">{formatMoney(due.paidAmount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                          <td className="p-4">
                            <StatusBadge status={dueStatusVariant(due.status)} label={due.status} />
                          </td>
                          <td className="p-4">
                            {due.lastReceipt ? (
                              <span className="text-xs font-mono text-text-secondary">{due.lastReceipt.receiptNumber}</span>
                            ) : <span className="text-text-muted">—</span>}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              {canPay && (
                                <Button size="sm" variant="primary" onClick={() => setRecordPaymentDue(due)}>
                                  Record
                                </Button>
                              )}
                              {canCancel && due.lastReceipt && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setCancelTarget({
                                      receiptId: due.lastReceipt!.receiptId,
                                      receiptNumber: due.lastReceipt!.receiptNumber,
                                      isSameDay: isSameDayReceipt(due.lastReceipt!.collectedAt),
                                    })
                                  }
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-text-muted">
            <span>Page {page}</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="secondary" size="sm" disabled={(duesData?.meta?.total ?? 0) <= page * 25} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </>
      ) : (
        <SchemeClosureTab schemeId={schemeId} schemeStatus={scheme?.status || 'CLOSING'} />
      )}

      {recordPaymentDue && (
        <RecordPaymentModal
          due={recordPaymentDue}
          open={!!recordPaymentDue}
          onClose={() => setRecordPaymentDue(null)}
          onSuccess={(receiptNumber) => {
            setRecordPaymentDue(null);
            setSuccessMessage(`Receipt ${receiptNumber} issued successfully.`);
            setTimeout(() => setSuccessMessage(''), 6000);
            void refetch();
          }}
        />
      )}

      {cancelTarget && (
        <CancelReceiptModal
          receiptId={cancelTarget.receiptId}
          receiptNumber={cancelTarget.receiptNumber}
          isSameDay={cancelTarget.isSameDay}
          open={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => {
            setCancelTarget(null);
            setSuccessMessage(
              cancelTarget.isSameDay
                ? `Receipt ${cancelTarget.receiptNumber} cancelled. Due reverted.`
                : `Cancellation of ${cancelTarget.receiptNumber} submitted for approval.`,
            );
            setTimeout(() => setSuccessMessage(''), 6000);
            void refetch();
          }}
        />
      )}
    </div>
  );
}
